import {
  Domain,
  getDefaultOrigin,
  getTLD,
  Region,
  tldToDomain,
  validateCountryLandingPageParam,
  validateRegion,
} from '@/utils/countries'
import {currencies, Currency, regionToCurrency} from '@/utils/currency'

import {isDevelopment} from '../../common'
import {Affiliate} from '../affiliate/config'
import {
  affiliateCookieName,
  affiliateParamName,
  countrySpecificParamName,
  currencyCookieName,
  currencyParamName,
  destinationCookieName,
  destinationParamName,
  originCookieName,
  originParamName,
} from '../constants/http'
import {CookieRecord} from './types'

/**
 * Raw request inputs, deliberately expressed as plain records so this module can
 * be called from a server component or the client alike.
 */
export type PreferenceSources = {
  host: string
  cookies: CookieRecord
  params: Record<string, string | undefined>
  // The visitor's country - a fact, not a cookie choice (utils/preferences/country.ts)
  country: string | undefined
}

export type ResolvedPreferences = {
  host: string
  country: string | undefined
  origin: Region
  destination: Region | null
  currency: Currency
  affiliate: Affiliate | null
  domain: Domain
}

const resolveDomain = (host: string): Domain => {
  const tld = getTLD(host) ?? 'com'
  return isDevelopment ? Domain.COM : tldToDomain[tld] ?? Domain.COM
}

const resolveCurrency = (
  cookieValue: string | undefined,
  paramValue: string | undefined,
  origin: Region,
): Currency => {
  // A campaign link's currency is an explicit choice, so it wins over one stored earlier
  if (paramValue && paramValue in currencies) {
    return paramValue as Currency
  }
  if (cookieValue && cookieValue in currencies) {
    return cookieValue as Currency
  }
  return regionToCurrency(origin)
}

const resolveAffiliate = (affiliate?: string | null): Affiliate | null => {
  const isValid = affiliate && Object.values(Affiliate).includes(affiliate as Affiliate)
  return isValid ? (affiliate as Affiliate) : null
}

/**
 * Single source of truth for turning cookies, query params, the host and the country into
 * the preferences a request should be rendered with. Pure: no React, no `js-cookie`,
 * no `next/headers`.
 */
export const resolvePreferences = ({
  host,
  cookies,
  params,
  country,
}: PreferenceSources): ResolvedPreferences => {
  const origin = getDefaultOrigin(
    host,
    validateRegion(cookies[originCookieName]),
    validateRegion(params[originParamName]),
    country,
  )

  const destination =
    validateCountryLandingPageParam(params[countrySpecificParamName]?.toUpperCase()) ||
    validateRegion(params[destinationParamName]) ||
    validateRegion(cookies[destinationCookieName]) ||
    null

  const affiliateValue = params[affiliateParamName] || cookies[affiliateCookieName] || null

  return {
    host,
    country,
    origin,
    destination,
    currency: resolveCurrency(cookies[currencyCookieName], params[currencyParamName], origin),
    affiliate: resolveAffiliate(affiliateValue),
    domain: resolveDomain(host),
  }
}
